import { isAxiosBenignLessonProgressTypeMismatch } from '@/utils/axiosBenignError';
import { isAxiosNetworkOrTimeoutError, isAxiosRequestCanceled } from '@/utils/isAxiosRequestCanceled';
import { isApexChartsElementNotFoundReason } from '@/utils/apexChartsBenign';
import { isDynamicImportChunkError } from '@/utils/chunkLoadError';

/**
 * @param {unknown} value
 * @returns {value is CustomEvent<any>}
 */
function isCustomEvent(value) {
    return typeof CustomEvent !== 'undefined' && value instanceof CustomEvent;
}

/**
 * Eventos de erro do Plyr chegam como CustomEvent com `detail.plyr`.
 * @param {import('@sentry/core').ErrorEvent} _event
 * @param {import('@sentry/core').EventHint | undefined} hint
 */
export function isPlyrMediaCustomErrorNoise(_event, hint) {
    const ex = hint?.originalException;
    if (isCustomEvent(ex) && ex.type === 'error') {
        const d = ex.detail;
        if (d != null && typeof d === 'object' && Object.prototype.hasOwnProperty.call(d, 'plyr')) {
            return true;
        }
    }

    const fromEvent = _event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    return /CustomEvent.*type=error/i.test(fromEvent);
}

function messageFromHint(hint) {
    const ex = hint?.originalException;
    if (typeof ex === 'string') return ex;
    if (ex && typeof ex === 'object') {
        if (typeof ex.message === 'string') return ex.message;
        if (ex.reason && typeof ex.reason.message === 'string') return ex.reason.message;
    }
    return '';
}

export function isBenignApexChartsElementNotFound(event, hint) {
    if (isApexChartsElementNotFoundReason(hint?.originalException)) {
        return true;
    }

    const fromHint = messageFromHint(hint);
    const fromEvent = event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    const msg = `${fromHint} ${fromEvent}`;
    if (!msg.includes('Element not found')) return false;

    return true;
}

/**
 * @type {import('@sentry/vue').BrowserOptions['beforeSend']}
 */
export function sentryBeforeSend(event, hint) {
    if (isPlyrMediaCustomErrorNoise(event, hint)) {
        return null;
    }
    if (isBenignApexChartsElementNotFound(event, hint)) {
        return null;
    }
    if (isAxiosRequestCanceled(hint?.originalException)) {
        return null;
    }
    if (isAxiosNetworkOrTimeoutError(hint?.originalException)) {
        return null;
    }
    if (isAxiosBenignLessonProgressTypeMismatch(hint?.originalException)) {
        return null;
    }
    if (isDynamicImportChunkError(hint?.originalException)) {
        return null;
    }

    const fromEvent = event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    if (isDynamicImportChunkError({ message: fromEvent })) {
        return null;
    }

    return event;
}
